import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from '../context/NotificationContext';
import { 
  Bell, 
  Check, 
  CheckCheck, 
  Trash2, 
  MessageSquare, 
  AlertTriangle, 
  Calendar, 
  Vote, 
  CreditCard,
  Settings,
  Filter,
  RefreshCw,
  Trophy,
  Star,
  Zap,
  Award
} from 'lucide-react';
import RewardPopup from '../components/RewardPopup';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NOTIFICATION_ICONS = {
  message: { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-100' },
  incident: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-100' },
  assemblee: { icon: Calendar, color: 'text-purple-500', bg: 'bg-purple-100' },
  survey: { icon: Vote, color: 'text-teal-500', bg: 'bg-teal-100' },
  charge: { icon: CreditCard, color: 'text-green-500', bg: 'bg-green-100' },
  system: { icon: Settings, color: 'text-gray-500', bg: 'bg-gray-100' },
  default: { icon: Bell, color: 'text-[#00D3A8]', bg: 'bg-[#00D3A8]/10' }
};

const PRIORITY_STYLES = {
  urgent: 'border-l-4 border-red-500 bg-red-50',
  high: 'border-l-4 border-orange-500 bg-orange-50',
  normal: '',
  low: 'opacity-75'
};

const NotificationsPage = () => {
  const { token } = useAuth();
  const { refreshNotifications } = useNotifications();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all');
  const [gamificationStats, setGamificationStats] = useState(null);
  const [showRewardPopup, setShowRewardPopup] = useState(false);
  const [newBadges, setNewBadges] = useState([]);
  const [selectedNotifications, setSelectedNotifications] = useState([]);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter === 'unread') params.append('unread_only', 'true');
      if (typeFilter !== 'all') params.append('notification_type', typeFilter);
      
      const response = await fetch(`${API_URL}/api/notifications?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token, filter, typeFilter]);

  const fetchGamificationStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/gamification/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGamificationStats(data);
      }
    } catch (error) {
      console.error('Error fetching gamification stats:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    fetchGamificationStats();
  }, [fetchNotifications, fetchGamificationStats]);

  const markAsRead = async (notificationIds) => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ notification_ids: notificationIds })
      });
      
      if (response.ok) {
        fetchNotifications();
        fetchGamificationStats();
        refreshNotifications();
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Check for new badges
        if (data.new_badges && data.new_badges.length > 0) {
          setNewBadges(data.new_badges);
          setShowRewardPopup(true);
        } else if (data.inbox_zero) {
          // Show Inbox Zero celebration even if badge already earned
          setNewBadges([{
            id: 'inbox_zero_celebration',
            name: 'Inbox Zero!',
            description: 'Toutes vos notifications sont lues',
            icon: '✨',
            color: '#EC4899'
          }]);
          setShowRewardPopup(true);
        }
        
        fetchNotifications();
        fetchGamificationStats();
        refreshNotifications();
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      const response = await fetch(`${API_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        fetchNotifications();
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationConfig = (type) => {
    return NOTIFICATION_ICONS[type] || NOTIFICATION_ICONS.default;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)} h`;
    if (diff < 604800000) return `Il y a ${Math.floor(diff / 86400000)} j`;
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="text-[#00D3A8]" />
            Notifications
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {unreadCount > 0 
              ? `${unreadCount} notification${unreadCount > 1 ? 's' : ''} non lue${unreadCount > 1 ? 's' : ''}`
              : 'Toutes vos notifications sont lues'
            }
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchNotifications()}
            className="p-2 text-gray-500 hover:text-[#00D3A8] hover:bg-gray-100 rounded-lg transition-colors"
            title="Rafraîchir"
          >
            <RefreshCw size={20} />
          </button>
          
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00C7A1] transition-colors"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck size={18} />
              <span className="hidden sm:inline">Tout marquer comme lu</span>
            </button>
          )}
        </div>
      </div>

      {/* Gamification Stats Card */}
      {gamificationStats && (
        <div className="bg-gradient-to-r from-[#00D3A8] to-[#00B894] rounded-xl p-4 mb-6 text-white">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Trophy size={24} />
              </div>
              <div>
                <p className="text-white/80 text-sm">Niveau {gamificationStats.level}</p>
                <p className="text-xl font-bold">{gamificationStats.total_xp} XP</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold">{gamificationStats.total_notifications_read}</p>
                <p className="text-white/80 text-xs">Notifications lues</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold flex items-center justify-center gap-1">
                  {gamificationStats.streak_days}
                  {gamificationStats.streak_days >= 3 && <Zap size={16} className="text-yellow-300" />}
                </p>
                <p className="text-white/80 text-xs">Jours consécutifs</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold">{gamificationStats.earned_badges?.length || 0}</p>
                <p className="text-white/80 text-xs">Badges</p>
              </div>
            </div>
          </div>
          
          {/* XP Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-xs text-white/80 mb-1">
              <span>Progression vers niveau {gamificationStats.level + 1}</span>
              <span>{gamificationStats.total_xp % 100} / 100 XP</span>
            </div>
            <div className="h-2 bg-white/20 rounded-full overflow-hidden">
              <div 
                className="h-full bg-white rounded-full transition-all duration-500"
                style={{ width: `${(gamificationStats.total_xp % 100)}%` }}
              />
            </div>
          </div>
          
          {/* Earned Badges Preview */}
          {gamificationStats.earned_badges?.length > 0 && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-white/80 text-sm">Badges:</span>
              <div className="flex gap-1">
                {gamificationStats.earned_badges.slice(0, 5).map((badge, idx) => (
                  <div 
                    key={idx}
                    className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-lg"
                    title={badge.name}
                  >
                    {badge.icon}
                  </div>
                ))}
                {gamificationStats.earned_badges.length > 5 && (
                  <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-xs font-bold">
                    +{gamificationStats.earned_badges.length - 5}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {['all', 'unread', 'read'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filter === f 
                  ? 'bg-white text-[#00D3A8] shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {f === 'all' && 'Toutes'}
              {f === 'unread' && 'Non lues'}
              {f === 'read' && 'Lues'}
            </button>
          ))}
        </div>
        
        <div className="flex items-center gap-1 text-sm text-gray-500">
          <Filter size={14} />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="bg-transparent border-0 focus:ring-0 text-sm cursor-pointer"
          >
            <option value="all">Tous les types</option>
            <option value="message">Messages</option>
            <option value="incident">Incidents</option>
            <option value="assemblee">Assemblées</option>
            <option value="survey">Sondages</option>
            <option value="charge">Charges</option>
          </select>
        </div>
      </div>

      {/* Notifications List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#00D3A8] border-t-transparent mx-auto"></div>
            <p className="text-gray-500 mt-2">Chargement...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Bell size={32} className="text-gray-300" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">Aucune notification</h3>
            <p className="text-gray-500 text-sm">
              {filter === 'unread' 
                ? 'Vous avez lu toutes vos notifications !' 
                : 'Vous n\'avez pas encore de notifications'}
            </p>
            {filter === 'unread' && (
              <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm">
                <Star size={16} />
                Inbox Zero atteint !
              </div>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((notif) => {
              const config = getNotificationConfig(notif.type);
              const IconComponent = config.icon;
              
              return (
                <div
                  key={notif.id}
                  className={`p-4 hover:bg-gray-50 transition-colors ${
                    !notif.read ? 'bg-blue-50/50' : ''
                  } ${PRIORITY_STYLES[notif.priority] || ''}`}
                  data-testid={`notification-item-${notif.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                      <IconComponent size={20} className={config.color} />
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h4 className={`font-medium ${!notif.read ? 'text-gray-900' : 'text-gray-700'}`}>
                            {notif.title}
                          </h4>
                          <p className="text-gray-500 text-sm mt-0.5 line-clamp-2">
                            {notif.content}
                          </p>
                        </div>
                        
                        {/* Unread indicator */}
                        {!notif.read && (
                          <span className="w-2 h-2 bg-[#00D3A8] rounded-full flex-shrink-0 mt-2"></span>
                        )}
                      </div>
                      
                      {/* Footer */}
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400">
                          {formatDate(notif.created_at)}
                        </span>
                        
                        <div className="flex items-center gap-1">
                          {!notif.read && (
                            <button
                              onClick={() => markAsRead([notif.id])}
                              className="p-1.5 text-gray-400 hover:text-[#00D3A8] hover:bg-[#00D3A8]/10 rounded transition-colors"
                              title="Marquer comme lu"
                            >
                              <Check size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notif.id)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Supprimer"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Reward Popup */}
      <RewardPopup
        isOpen={showRewardPopup}
        onClose={() => {
          setShowRewardPopup(false);
          setNewBadges([]);
        }}
        badges={newBadges}
      />
    </div>
  );
};

export default NotificationsPage;
